import { Directive, ElementRef, OnDestroy, NgZone } from '@angular/core';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * Auto-sizes AG-Grid pagination to fill available container height.
 *
 * Selector matches ALL <ag-grid-angular> elements automatically.
 * Internally skips grids that should NOT be auto-sized:
 *   - domLayout='autoHeight'
 *   - Inline style with pixel height (modal grids)
 *   - Container too small (<200px)
 *
 * If the user manually changes the page size selector, auto-sizing
 * stops for that grid instance (respects user override).
 */
@Directive({
  selector: 'ag-grid-angular'
})
export class AutoPageSizeDirective implements OnDestroy {
  private gridApi: any = null;
  private resizeSub: Subscription | null = null;
  private userOverridden = false;
  private gridReadyListener: ((e: any) => void) | null = null;
  private paginationListener: ((e: any) => void) | null = null;

  constructor(private el: ElementRef, private zone: NgZone) {
    // AG-Grid fires gridReady as an Angular output event.
    // We listen on the DOM element for the 'gridReady' custom event.
    this.gridReadyListener = (event: any) => {
      this.onGridReady(event);
    };

    // Use a small delay to let AG-Grid initialize
    setTimeout(() => this.tryAttach(), 100);
  }

  private tryAttach(): void {
    // Directive placeholder — AG-Grid v31 API access is handled per-component
  }

  private onGridReady(event: any): void {
    this.gridApi = event.api || event.detail?.api;
    if (!this.gridApi) return;

    if (this.shouldSkip()) return;

    // Listen for user-initiated page size changes
    this.gridApi.addEventListener('paginationChanged', (e: any) => {
      if (this.userOverridden) return;
    });

    // Calculate initial page size
    this.zone.runOutsideAngular(() => {
      setTimeout(() => this.calculateAndApply(), 300);

      // Recalculate on window resize
      this.resizeSub = fromEvent(window, 'resize').pipe(
        debounceTime(300)
      ).subscribe(() => {
        if (!this.userOverridden) this.calculateAndApply();
      });
    });

    // Listen for the page size selector being used by the user
    // AG-Grid's paginationPageSizeSelector triggers a DOM event we can capture
    const pagerEl = this.el.nativeElement.querySelector('.ag-paging-page-size');
    if (pagerEl) {
      this.paginationListener = () => { this.userOverridden = true; };
      pagerEl.addEventListener('change', this.paginationListener);
    }
  }

  private shouldSkip(): boolean {
    if (!this.gridApi) return true;

    // Skip autoHeight grids
    try {
      const domLayout = this.gridApi.getGridOption?.('domLayout');
      if (domLayout === 'autoHeight') return true;
    } catch { /* ignore */ }

    // Skip grids with inline pixel height (modal grids)
    const el = this.el.nativeElement;
    const inlineHeight = el.style?.height;
    if (inlineHeight && inlineHeight.includes('px')) return true;

    // Skip if container is too small (likely a modal or collapsed section)
    const container = el.closest('.agGridTable') || el.closest('.single-container') || el;
    if (container.clientHeight < 200) return true;

    // Skip if pagination is disabled
    try {
      const pagination = this.gridApi.getGridOption?.('pagination');
      if (pagination === false) return true;
    } catch { /* ignore */ }

    return false;
  }

  private calculateAndApply(): void {
    if (!this.gridApi || this.userOverridden) return;
    if (this.shouldSkip()) return;

    const el = this.el.nativeElement;
    const container = el.closest('.agGridTable') || el.closest('.single-container') || el;
    const containerHeight = container.clientHeight;

    if (containerHeight < 200) return;

    // Get actual row height from the grid
    let rowHeight = 40; // default
    try {
      const sizes = this.gridApi.getSizesForCurrentTheme?.();
      if (sizes?.rowHeight) rowHeight = sizes.rowHeight;
    } catch { /* use default */ }

    // Header height (AG-Grid header row)
    const headerEl = el.querySelector('.ag-header');
    const headerHeight = headerEl?.clientHeight || 42;

    // Pagination panel height
    const pagerEl = el.querySelector('.ag-paging-panel');
    const pagerHeight = pagerEl?.clientHeight || 48;

    // Calculate optimal page size
    const availableForRows = containerHeight - headerHeight - pagerHeight - 4; // 4px buffer
    const optimalPageSize = Math.max(5, Math.floor(availableForRows / rowHeight));

    // Only update if the calculated size is meaningfully different from current
    try {
      const currentPageSize = this.gridApi.paginationGetPageSize?.();
      if (currentPageSize === optimalPageSize) return;
      this.gridApi.paginationSetPageSize(optimalPageSize);
    } catch { /* grid may not support pagination */ }
  }

  ngOnDestroy(): void {
    this.resizeSub?.unsubscribe();

    const pagerEl = this.el.nativeElement.querySelector('.ag-paging-page-size');
    if (pagerEl && this.paginationListener) {
      pagerEl.removeEventListener('change', this.paginationListener);
    }
  }
}
